import { Box, CircularProgress, Grid, IconButton, List, ListItem, ListItemText, Skeleton, SwipeableDrawer, Typography } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { BookGridContainer, BookImage, BookItemBox, BookTitleTypography, HeaderBox, MainContentBox, SkeletonBookGridContainer, SkeletonBookItemBox } from './MobileBookPage';
import { useLocation, useNavigate } from 'react-router-dom';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import LineWeightOutlinedIcon from '@mui/icons-material/LineWeightOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import SubjectOutlinedIcon from '@mui/icons-material/SubjectOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';
import AudiosService from '../../Services/Audios';
import audioProductIcon from "../../../src/svg/audioHome.svg";
import pravachanProductIcon from "../../../src/svg/pravachanHome.svg";
import dailySatsangProductIcon from "../../../src/svg/DailySatsang.svg";
import withoutLyricsIcon from "../../Images/Image/withoutLyrics.png";
import withLyricsIcon from "../../Images/Image/withLyrics.png";
import { useAudio } from '../../Contexts/AudiosContext';
import { useDownloadApp } from '../../Contexts/DownloadAppContext';
interface ViewProps {
  isGridView: boolean;
}
// Styled components
const FilterBarBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  backgroundColor: '#fff',
  padding: theme.spacing(1),
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[1],
  margin: theme.spacing(1, 2, 1, 2),
}));

const FilterOptionItem = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  padding: '4px 10px',
  borderRadius: 8,
  transition: 'background 0.2s',
  '&:hover': {
    background: '#f5f5f5',
  },
}));

const PAGE_SIZE = 20;

const getProductTypeIcon = (productType?: string) => {
  if (productType === "DailySatsang") return dailySatsangProductIcon;
  if (productType === "audios") return audioProductIcon;
  return pravachanProductIcon;
};

const hasLyrics = (item: any) =>
  item?.lyricsHash != null &&
  item?.lyricsHash !== "" &&
  item?.lyricsHash !== "null";

const getLyricsAvatarIcon = (item: any) =>
  hasLyrics(item) ? withLyricsIcon : withoutLyricsIcon;

const getListItemAvatarIcon = (item: any, contentType?: string) => {
  if (contentType === "DailySatsang") return dailySatsangProductIcon;
  return getLyricsAvatarIcon(item);
};

const audioListMetaIconSx = {
  fontSize: 16,
  color: '#888',
  mt: 0.2,
  flexShrink: 0,
};

interface AudioListItemContentProps {
  productType?: string;
  name: string;
  author?: string;
  description?: string;
  duration?: string;
}

const AudioListItemContent: React.FC<AudioListItemContentProps> = ({
  productType,
  name,
  author,
  description,
  duration,
}) => (
  <Box sx={{ flex: 1, minWidth: 0, pr: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, width: '100%' }}>
      <Box
        component="img"
        src={getProductTypeIcon(productType)}
        alt=""
        sx={{ width: 18, height: 18, flexShrink: 0, mt: 0.25 }}
      />
      <Typography
        component="div"
        sx={{
          fontFamily: 'chanakyaUni',
          fontSize: 18,
          fontWeight: 600,
          color: '#4b2e2e',
          lineHeight: 1.3,
          flex: 1,
          minWidth: 0,
          wordBreak: 'break-word',
        }}
      >
        {name}
      </Typography>
    </Box>

    {author?.trim() && (
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, width: '100%' }}>
        <PersonOutlineOutlinedIcon sx={audioListMetaIconSx} />
        <Typography
          component="div"
          sx={{
            fontSize: 13,
            color: '#535353',
            fontFamily: 'chanakyaUni',
            fontWeight: 400,
            lineHeight: 1.3,
            flex: 1,
            minWidth: 0,
            wordBreak: 'break-word',
          }}
        >
          {author}
        </Typography>
      </Box>
    )}

    {description?.trim() && (
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, width: '100%' }}>
        <SubjectOutlinedIcon sx={audioListMetaIconSx} />
        <Typography
          component="div"
          sx={{
            fontSize: 12,
            color: 'gray',
            lineHeight: 1.4,
            flex: 1,
            minWidth: 0,
            wordBreak: 'break-word',
            whiteSpace: 'pre-line',
          }}
        >
          {description}
        </Typography>
      </Box>
    )}

    {duration && (
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.5, width: '100%', pr: 0.5 }}>
        <AccessTimeOutlinedIcon sx={{ fontSize: 14, color: 'gray' }} />
        <Typography component="span" sx={{ fontSize: 12, color: 'gray' }}>
          {duration}
        </Typography>
      </Box>
    )}
  </Box>
);

const sortOptions = [
  { text: "General_tr", value: "0" },
  { text: "Popular_tr", value: "3" },
  { text: "Latest_tr", value: "2" },
  { text: "Title_tr", value: "1" },
];

const MobileAudioListPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { playPravachan, setpravachanId, currentAudio, setMbAudioId } = useAudio();
  const { setShowDownloadPopup } = useDownloadApp();
  const [isGridView, setIsGridView] = useState(false);
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [audioList, setAudioList] = useState<any[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [items, setItems] = useState<any[]>([]);
  const location: any = useLocation();

  const [sortValue, setSortValue] = useState(location?.state?.sortOption || "3");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [isSortDrawerOpen, setIsSortDrawerOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const observerRef = useRef<HTMLDivElement>(null);
  const productType = location?.state?.type || "pravachans";
  const isShowFilter = false; // set true to restore filter/sort bar

  const fetchAudio = useCallback(async (pageNum: number, append: boolean) => {
    if (append) setIsLoadingMore(true);
    else setIsLoading(true);

    try {
      if (location?.state?.audioId) {
        const type = location?.state?.type || "audios";
        const res = await AudiosService.getAudiosById(location.state.audioId, type);

        if (res?.status && res?.result) {
          const singleItem = res.result;
          setAudioList([singleItem]);
          setTotalItems(1);
          setHasMore(false);
          if (!append) {
            setItems([{ title: singleItem.name }]);
          }
        }
      } else {
        const contentType = location?.state?.type || "pravachans";

        const res = contentType === "DailySatsang"
          ? await AudiosService.getAudioPodcast(
              pageNum * PAGE_SIZE,
              PAGE_SIZE,
              sortValue,
              location?.state?.month || "0",
              location?.state?.year || "0"
            )
          : await AudiosService.getAudios(
              pageNum * PAGE_SIZE,
              PAGE_SIZE,
              false,
              location?.state?.categoryId || "",
              location?.state?.authorId || "",
              '',
              sortValue,
              '',
              location?.state?.isSpecial || false,
              contentType,
              0,
              location?.state?.month || "",
              location?.state?.year || "",
              '',
              location?.state?.ptime || "",
              location?.state?.isOther || false,
            );

        if (res?.status && res?.result) {
          const newItems = res.result.items || [];
          const total = res?.result?.totalRecords || 0;

          if (append) {
            setAudioList(prev => [...prev, ...newItems]);
          } else {
            setAudioList(newItems);
          }

          setTotalItems(total);
          setHasMore((pageNum * PAGE_SIZE + newItems.length) < total);

          if (!append) {
            setItems(newItems.map((item: any) => ({ title: item.name })));
          }
        }
      }
    } catch (err) {
      console.error("Error fetching audios", err);
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, [sortValue, sortOrder, location?.state?.categoryId, location?.state?.authorId,
      location?.state?.isSpecial, location?.state?.isOther, location?.state?.type, location?.state?.month,
      location?.state?.year, location?.state?.ptime, location?.state?.audioId]);

  useEffect(() => {
    setPage(0);
    setHasMore(true);
    fetchAudio(0, false);
  }, [fetchAudio]);

  const loadMore = useCallback(async () => {
    if (!hasMore || isLoadingMore || isLoading) return;
    const nextPage = page + 1;
    setPage(nextPage);
    await fetchAudio(nextPage, true);
  }, [page, hasMore, isLoadingMore, isLoading, fetchAudio]);

  useEffect(() => {
    const node = observerRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoadingMore && !isLoading) {
          loadMore();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [loadMore, hasMore, isLoadingMore, isLoading]);

  const handleSortOptionSelect = (value: string) => {
    setSortValue(value);
    setIsSortDrawerOpen(false);
  };

  const filterOptionsBar = [
    { text: "Sort_tr", icon: <SwapVertIcon />, type: "action", onClick: () => setIsSortDrawerOpen(true) },
    { text: "Filter_tr", icon: <FilterAltOutlinedIcon />, type: "static" },
    {
      text: sortOrder === "asc" ? "ascending_date_tr" : "descending_date_tr",
      icon: <SwapVertIcon sx={{ transform: sortOrder === "desc" ? "rotateX(180deg)" : "none" }} />,
      type: "sort-toggle",
      onClick: () => setSortOrder(prev => prev === "asc" ? "desc" : "asc"),
    },
  ];

  const numberOfSkeletons = isGridView ? 9 : 5;
  function stripHtmlTags(htmlString: any) {
    const doc = new DOMParser().parseFromString(htmlString, 'text/html');
    return doc.body.textContent || "";
  }

  const BookImage = styled('img', {
    shouldForwardProp: (prop) => prop !== 'isGridView'
  })<ViewProps>(({ isGridView }) => ({
    width: isGridView ? "100%" : "60px",
    height: isGridView ? "120px" : "80px",
    objectFit: "contain",
    marginBottom: isGridView ? '8px' : '0',
    flexShrink: 0,
  }));
  // useEffect(()=>{

  //   console.log(items,"items=======aaaaaaaa")

  // },[])

  return (
    <>
      <Box
        sx={{
          backgroundColor: "#F2F0EF",
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Header Section */}
        <HeaderBox>
          <IconButton onClick={() => navigate(-1)} sx={{ position: 'absolute', left: 16, top: 35, p: 0 }}>
            <ArrowBackIosIcon sx={{ color: "white", fontSize: '18px' }} />
          </IconButton>
          <IconButton onClick={() => navigate("/")} sx={{ position: 'absolute', left: 48, top: 35, p: 0 }}>
            <HomeOutlinedIcon sx={{ color: "white", fontSize: '24px' }} />
          </IconButton>
          <IconButton
            onClick={() => setIsGridView((prev: any) => !prev)}
            sx={{ position: 'absolute', right: 16, top: 35, p: 0 }}
          >
            {isGridView ? (
              <ViewListIcon sx={{ color: "white", fontSize: '20px' }} />
            ) : (
              <ViewModuleIcon sx={{ color: "white", fontSize: '20px' }} />
            )}
          </IconButton>
          <Typography variant="caption" sx={{ color: "#ffffffdd", opacity: 0.9, mb: 0.5 }}>
            {t("shrihari_tr")}
          </Typography>
          <Typography variant="h6" sx={{ color: "#ffffffdd", opacity: 0.9, fontSize: 15 }}>
            {(() => {
              const type = location?.state?.type || "pravachans";
              const authorName = location?.state?.authorName;
              const categoryName = location?.state?.categoryName;
              let baseTitle = type === "pravachans" ? t("Pravachan_tr") : type === "DailySatsang" ? t("daily_satsang_tr") : t("Audios_tr");
              const displayName = authorName || categoryName;
              return displayName ? `${baseTitle} (${displayName})` : baseTitle;
            })()}
          </Typography>
        </HeaderBox>

        {/* Filter/Sort Bar Section */}
        {isShowFilter && (
          <FilterBarBox>
            {filterOptionsBar.map((option, index) => (
              <React.Fragment key={option.text}>
                <FilterOptionItem
                  onClick={option.onClick}
                  sx={{
                    cursor: option.type === 'action' || option.type === 'sort-toggle' ? 'pointer' : 'default'
                  }}
                >
                  {React.cloneElement(option.icon, { sx: { color: "lightslategray", fontSize: '20px' } })}
                  <Typography variant="body2" sx={{ color: "lightslategray", fontSize: '13px' }}>
                    {t(option.text)}
                  </Typography>
                </FilterOptionItem>
                {index < filterOptionsBar.length - 1 && <Box sx={{ width: '1px', height: '60%', backgroundColor: '#e0e0e0' }} />}
              </React.Fragment>
            ))}
          </FilterBarBox>
        )}

        {/* Main Content Area */}

        <Typography
          variant="body2"
          sx={{
            textAlign: 'center',
            mt: 0.5,
            fontWeight: 500,
            fontFamily: "chanakyaUni",
            fontSize: 16,
            height: 15,
            padding: -3
          }}
        >
          {t("Total_Records_tr")}: {totalItems}
        </Typography>


        <MainContentBox>

          {isLoading ? (
            <SkeletonBookGridContainer container spacing={1} isGridView={isGridView}>
              {Array.from(new Array(numberOfSkeletons)).map((_, index) => (
                <Grid
                  item
                  xs={isGridView ? 4 : 12}
                  sm={isGridView ? 3 : 12}
                  md={isGridView ? 2 : 12}
                  key={index}
                >
                  <SkeletonBookItemBox isGridView={isGridView}>
                    <Skeleton
                      variant="rectangular"
                      width={isGridView ? "100%" : "60px"}
                      height={isGridView ? "120px" : "80px"}
                      sx={{
                        marginBottom: isGridView ? '8px' : '0',
                        flexShrink: 0,
                      }}
                    />
                    <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                      <Skeleton variant="text" sx={{ fontSize: isGridView ? '12px' : '14px', width: '80%', mb: 0.5 }} />
                      {!isGridView && <Skeleton variant="text" sx={{ fontSize: '12px', width: '60%' }} />}
                    </Box>
                  </SkeletonBookItemBox>
                </Grid>
              ))}
            </SkeletonBookGridContainer>
          ) : audioList.length > 0 ? (
            <BookGridContainer container spacing={0} isGridView={isGridView}>
              {audioList?.map((item: any, index: number) => {
                const descriptionText = item.description ? stripHtmlTags(item.description).trim() : "";
                const duration = item.audioLength || item.pravachanLength;

                return (
                <Grid
                  sx={{ padding: isGridView ? "2px" : "0px" }}
                  item
                  xs={isGridView ? 4 : 12}
                  sm={isGridView ? 3 : 12}
                  md={isGridView ? 2 : 12}
                  key={item.id}
                  // onClick={() =>
                  // {
                  //   // debugger;
                  //   localStorage.setItem("type","pravachans") ;
                  //   setpravachanId(item.id);
                  //   navigate("/audioPlayerPage",
                  //     { state: {
                  //       audioId: item.id, 
                  //       audioName: item?.name, 
                  //       authorId: location?.state?.authorId, 
                  //       authorName: item?.author, 
                  //       orderNo: location?.state?.orderNo,
                  //       index:index,

                  //     } })}}

                  onClick={() => {
                    console.log(localStorage.getItem("pravachanId"), "pravachanIddddddddd");
                    //  console.log(localStorage.getItem("AudioId"),"currentAudiocurrentAudiotype");

                    // if (location?.state?.type === "pravachans") {
                    //   localStorage.setItem("type", location?.state?.type);
                    //   localStorage.setItem("pravachanId", item.id);
                    //   localStorage.setItem("AudioId", "");
                    // } else if (location?.state?.type === "audios") {
                    //   localStorage.setItem("type", location?.state?.type);
                    //   localStorage.setItem("pravachanId", "");
                    //   localStorage.setItem("AudioId", item.id);
                    // }

                    const contentType = location?.state?.type;

                      if (contentType === "audios") {
                        localStorage.setItem("type", "audios");
                        localStorage.setItem("pravachanId", "");
                        localStorage.setItem("audioId", item.id);
                        setMbAudioId(item.id);
                        setpravachanId("");
                      } else {
                        localStorage.setItem("type", "pravachans");
                        localStorage.setItem("pravachanId", item.id);
                        localStorage.setItem("audioId", "");
                        setMbAudioId("");
                        setpravachanId(item.id);
                      }

                  
                  
                    // navigate("/audioPlayerPage", {
                    //   state: {
                    //     audioId: item.id,
                    //     audioName: item?.name,
                    //     authorId: location?.state?.authorId,
                    //     authorName: item?.author,
                    //     orderNo: location?.state?.orderNo,
                    //     index: index,
                    //   },
                    // });

                    setShowDownloadPopup(true);

                  }}

                >
                  {/* <BookItemBox isGridView={isGridView} onClick={() => handleItemClick(item.id)}> */}
                  <BookItemBox isGridView={isGridView} sx={{ margin: isGridView ? 0.1 : 0.3, padding: 1, boxShadow: "none" }} >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: isGridView ? 'center' : 'flex-start',
                        flexShrink: 0,
                        width: isGridView ? '100%' : 50,
                        minWidth: isGridView ? undefined : 50,
                        mb: isGridView ? '2px' : 0,
                        pt: isGridView ? 0 : 0.5,
                      }}
                    >
                      <Box
                        sx={{
                          width: 50,
                          height: 50,
                          borderRadius: '50%',
                          overflow: 'hidden',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          bgcolor: '#fff',
                        }}
                      >
                        <Box
                          component="img"
                          src={getListItemAvatarIcon(item, productType)}
                          alt=""
                          onError={(e) => { e.currentTarget.src = withoutLyricsIcon; }}
                          sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                          }}
                        />
                      </Box>
                    </Box>
                    <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', minWidth: 0, width: '100%' }}>
                      {isGridView ? (
                        <BookTitleTypography variant="body2" isGridView={isGridView}>
                          {item.name}
                        </BookTitleTypography>
                      ) : (
                        <AudioListItemContent
                          productType={productType}
                          name={item.name}
                          author={item.author}
                          description={descriptionText}
                          duration={duration}
                        />
                      )}
                    </Box>
                    {isGridView && item.author?.trim() && (
                      <p style={{ fontSize: 11, color: '#535353', margin: '2px 0 0 0', fontFamily: 'chanakyaUni', textAlign: 'center', display: 'block', overflow: 'visible', WebkitLineClamp: 'unset', WebkitBoxOrient: 'unset' }}>
                        {item.author}
                      </p>
                    )}



                  </BookItemBox>
                </Grid>
              );
              })}
            </BookGridContainer>
          ) : (
            <Typography variant="body1" sx={{ textAlign: 'center', mt: 4, color: 'text.secondary' }}>
              {/* {t(isKalyansPage ? (isKalpataruBook ? "No_kalpataru_found" : "No_kalyans_found") : (isVivekVaniPage ? "No_vivekvani_found" : (magazineType ? "No_geetgovind_found" : "No_books_found")))} */}
            </Typography>
          )}

          {isLoadingMore && (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
              <CircularProgress size={24} />
            </Box>
          )}
          {hasMore && <Box ref={observerRef} sx={{ height: 1 }} />}
        </MainContentBox>
      </Box>

      {isShowFilter && (
        <SwipeableDrawer
          anchor="bottom"
          open={isSortDrawerOpen}
          onClose={() => setIsSortDrawerOpen(false)}
          onOpen={() => setIsSortDrawerOpen(true)}
        >
          <Box sx={{ pb: 2 }} role="presentation">
            <List>
              <ListItem>
                <ListItemText primary={<Typography variant="h6" sx={{ fontWeight: 'bold' }}>{t("Sort_tr")}</Typography>} />
              </ListItem>
              {sortOptions.map((option) => (
                <ListItem
                  button
                  key={option.value}
                  onClick={() => handleSortOptionSelect(option.value)}
                  sx={{
                    backgroundColor: sortValue === option.value ? '#f0f0f0' : 'transparent',
                    '&:hover': { backgroundColor: '#e0e0e0' },
                  }}
                >
                  <ListItemText primary={t(option.text)} />
                </ListItem>
              ))}
            </List>
          </Box>
        </SwipeableDrawer>
      )}
    </>
  );
};

export default MobileAudioListPage;